import React, { useState } from 'react';
import {
  Search,
  ExternalLink,
  Plus,
  Trash2,
  Copy,
  Check,
  FileText,
  Sparkles,
  AlertTriangle,
  Layers,
  ListOrdered
} from 'lucide-react';
import { ContentPlanningProject, ContentPlanningPage, PageCompetitorHeading, PagePainPoint } from '../../types';
import { Button } from '../UI/Button';

interface PageContentResearchWorkstationProps {
  project: ContentPlanningProject;
  onSaveProject: (project: ContentPlanningProject) => void;
}

interface CompetitorCardInputProps {
  pageId: string;
  compNum: number;
  compData?: PageCompetitorHeading;
  onUpdate: (compNum: number, nameOrUrl: string, headingsText: string) => void;
}

const CompetitorCardInput: React.FC<CompetitorCardInputProps> = ({ pageId, compNum, compData, onUpdate }) => {
  const [nameOrUrl, setNameOrUrl] = useState(compData?.nameOrUrl || '');
  const [headingsText, setHeadingsText] = useState((compData?.headings || []).join('\n'));

  // Sync state whenever pageId or compData changes
  React.useEffect(() => {
    setNameOrUrl(compData?.nameOrUrl || '');
    setHeadingsText((compData?.headings || []).join('\n'));
  }, [pageId, compData?.nameOrUrl, compData?.headings]);

  const handleBlur = () => {
    onUpdate(compNum, nameOrUrl, headingsText);
  };

  return (
    <div
      style={{
        background: '#090e1a',
        border: '1px solid #1e293b',
        borderRadius: '12px',
        padding: '16px',
        transition: 'all 0.15s ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.25)', padding: '2px 8px', borderRadius: '6px' }}>
          COMPETITOR #{compNum}
        </span>
        <span style={{ fontSize: '11px', color: compData?.headings?.length ? '#10b981' : '#64748b', fontWeight: 700 }}>
          {compData?.headings?.length || 0} Headings Added
        </span>
      </div>

      {/* Competitor Name or URL Input */}
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#cbd5e1', marginBottom: '4px' }}>
          Competitor Name / Website URL
        </label>
        <input
          type="text"
          placeholder={`e.g. Competitor ${compNum} (https://competitor${compNum}.com)`}
          value={nameOrUrl}
          onChange={e => setNameOrUrl(e.target.value)}
          onBlur={handleBlur}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '8px',
            background: '#070c16',
            border: '1px solid #1e293b',
            color: '#ffffff',
            fontSize: '13px',
          }}
        />
      </div>

      {/* Headings Input Textarea */}
      <div>
        <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#cbd5e1', marginBottom: '4px' }}>
          Headings (H1, H2, H3 - One per line)
        </label>
        <textarea
          rows={3}
          placeholder={`Paste raw H1/H2/H3 headings for Competitor ${compNum}...\ne.g. H1: Top Truck Spare Parts Supplier\nH2: Fast Island Delivery\nH2: Fleet Replacement Components`}
          value={headingsText}
          onChange={e => setHeadingsText(e.target.value)}
          onBlur={handleBlur}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '8px',
            background: '#070c16',
            border: '1px solid #1e293b',
            color: '#ffffff',
            fontSize: '12.5px',
            fontFamily: 'monospace',
            resize: 'vertical',
          }}
        />
      </div>
    </div>
  );
};

// Helper function to build normalized & consolidated combined competitor headings dataset
const generateCombinedHeadings = (competitors: PageCompetitorHeading[]): string[] => {
  const headingMap = new Map<string, { tag: string; cleanText: string; count: number }>();

  competitors.forEach(comp => {
    (comp.headings || []).forEach(rawLine => {
      const line = rawLine.trim();
      if (!line) return;

      let tag = 'H2';
      let cleanText = line;

      // Extract tag if present (H1:, H2:, H3:, h1, h2, h3)
      const tagMatch = line.match(/^(H1|H2|H3|h1|h2|h3)[:.\-\s]+/i);
      if (tagMatch) {
        tag = tagMatch[1].toUpperCase();
        cleanText = line.substring(tagMatch[0].length).trim();
      }

      if (!cleanText) return;

      const key = `${tag}:${cleanText.toLowerCase()}`;
      const existing = headingMap.get(key);

      if (existing) {
        existing.count += 1;
      } else {
        headingMap.set(key, { tag, cleanText, count: 1 });
      }
    });
  });

  const combinedLines: string[] = [];
  headingMap.forEach(item => {
    const freqSuffix = item.count > 1 ? ` (Covered by ${item.count} competitors)` : '';
    combinedLines.push(`${item.tag}: ${item.cleanText}${freqSuffix}`);
  });

  return combinedLines;
};

// Helper to determine page research status
const getPageResearchStatus = (page: ContentPlanningPage): { label: string; color: string; bg: string; border: string } => {
  const activeComps = (page.competitorHeadings || []).filter(c => (c.headings && c.headings.length > 0) || c.nameOrUrl?.trim()).length;
  if (activeComps === 0) {
    return { label: 'Not Started', color: '#94a3b8', bg: '#090e1a', border: '#1e293b' };
  } else if (activeComps === 1) {
    return { label: 'In Progress', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)' };
  } else {
    return { label: 'Ready for Outline', color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.3)' };
  }
};

export const PageContentResearchWorkstation: React.FC<PageContentResearchWorkstationProps> = ({
  project,
  onSaveProject,
}) => {
  const pages = project.pages || [];
  const [selectedPageId, setSelectedPageId] = useState<string>(pages[0]?.id || '');
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [copiedCombined, setCopiedCombined] = useState(false);

  // New Pain Point Form State
  const [newPainTitle, setNewPainTitle] = useState('');
  const [newPainDesc, setNewPainDesc] = useState('');
  const [isAddPainModalOpen, setIsAddPainModalOpen] = useState(false);

  // Find currently selected page
  const selectedPage = pages.find(p => p.id === selectedPageId) || pages[0];

  if (!selectedPage) {
    return (
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '48px', textAlign: 'center' }}>
        <FileText size={42} color="#334155" style={{ margin: '0 auto 16px auto' }} />
        <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: '0 0 8px 0' }}>
          No Pages Planned in this Project
        </h3>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Please go back to Step 1 and add pages to your content plan first.
        </p>
      </div>
    );
  }

  // Handle Google Search Trigger using Seed Keyword
  const handleOpenGoogleSearch = () => {
    if (!selectedPage.primaryKeyword) return;
    const query = encodeURIComponent(selectedPage.primaryKeyword);
    window.open(`https://www.google.com/search?q=${query}`, '_blank');
  };

  // Helper to update selected page data inside project
  const updateCurrentPage = (updatedPageFields: Partial<ContentPlanningPage>) => {
    const updatedPages = pages.map(p => {
      if (p.id === selectedPage.id) {
        return {
          ...p,
          ...updatedPageFields,
        };
      }
      return p;
    });

    onSaveProject({
      ...project,
      pages: updatedPages,
      updatedAt: new Date().toISOString(),
    });
  };

  // Competitor Headings Handlers
  const competitorHeadings: PageCompetitorHeading[] = selectedPage.competitorHeadings || [];

  const handleUpdateCompetitor = (competitorNum: number, nameOrUrl: string, headingsText: string) => {
    const headingsList = headingsText
      .split('\n')
      .map(h => h.trim())
      .filter(h => h.length > 0);

    const existingIndex = competitorHeadings.findIndex(c => c.competitorNumber === competitorNum);
    let updatedList = [...competitorHeadings];

    if (existingIndex !== -1) {
      updatedList[existingIndex] = {
        ...updatedList[existingIndex],
        nameOrUrl,
        headings: headingsList,
      };
    } else {
      updatedList.push({
        id: `comp-${Date.now()}-${competitorNum}`,
        competitorNumber: competitorNum,
        nameOrUrl,
        headings: headingsList,
      });
    }

    // Sort by competitor number 1..10
    updatedList.sort((a, b) => a.competitorNumber - b.competitorNumber);

    // Compute combined headings dataset & status
    const combined = generateCombinedHeadings(updatedList);
    const activeCompCount = updatedList.filter(c => (c.headings && c.headings.length > 0) || c.nameOrUrl?.trim()).length;
    const researchStatus = activeCompCount === 0 ? 'not_started' : activeCompCount < 2 ? 'in_progress' : 'ready';

    updateCurrentPage({
      competitorHeadings: updatedList,
      combinedCompetitorHeadings: combined,
      researchStatus,
    });
  };

  // Combined Headings dataset (live generated if empty)
  const combinedHeadingsList = (selectedPage.combinedCompetitorHeadings && selectedPage.combinedCompetitorHeadings.length > 0)
    ? selectedPage.combinedCompetitorHeadings
    : generateCombinedHeadings(competitorHeadings);

  const handleCopyCombinedHeadings = () => {
    if (combinedHeadingsList.length === 0) return;
    navigator.clipboard.writeText(combinedHeadingsList.join('\n'));
    setCopiedCombined(true);
    setTimeout(() => setCopiedCombined(false), 2500);
  };

  // Pain Points Handlers
  const painPoints: PagePainPoint[] = selectedPage.painPoints || [];

  const handleAddPainPoint = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPainTitle.trim()) return;

    const newPt: PagePainPoint = {
      id: `pain-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      title: newPainTitle.trim(),
      description: newPainDesc.trim() || undefined,
    };

    updateCurrentPage({ painPoints: [...painPoints, newPt] });
    setNewPainTitle('');
    setNewPainDesc('');
    setIsAddPainModalOpen(false);
  };

  const handleDeletePainPoint = (painId: string) => {
    const updated = painPoints.filter(p => p.id !== painId);
    updateCurrentPage({ painPoints: updated });
  };

  // State for GPT Output Auto-Fill
  const [pastedGptOutput, setPastedGptOutput] = useState('');
  const [autoFillStatus, setAutoFillStatus] = useState<string | null>(null);

  // GPT Prompt Helper for Customer Pain Points / Research Insights
  const gptPromptText = `Act as an expert SEO researcher and customer intent strategist.
I am building a webpage for "${selectedPage.pageName}" targeting the seed keyword "${selectedPage.primaryKeyword || 'Services'}" in location "${project.targetedLocation}".

What are 5-8 real-world customer pain points, risks, anxieties, objections, and frustrations buyers face when searching for this topic?

RESEARCH GUIDELINES:
- Focus on real customer problems, purchasing risks, and service expectations.
- Do NOT invent fake customer testimonials or unverified business claims.
- Return structured insights for research hypothesis validation.

STRICT OUTPUT FORMAT REQUIRED:
Return ONLY a valid JSON array of objects with keys "title" and "description":
[
  {
    "title": "Clear Pain Point or Risk Title",
    "description": "Explanation of customer anxiety or service frustration."
  }
]`;

  const handleCopyGptPrompt = () => {
    navigator.clipboard.writeText(gptPromptText);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 2500);
  };

  // Auto-Fill Pain Points from GPT Output
  const handleAutoFillPainPoints = () => {
    if (!pastedGptOutput.trim()) return;

    try {
      let raw = pastedGptOutput.trim();

      // Strip markdown code block fences if present
      if (raw.includes('```')) {
        raw = raw.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '').trim();
      }

      let parsed: any[] = [];
      try {
        parsed = JSON.parse(raw);
      } catch {
        // Fallback parser if GPT returned text or bullet format
        const lines = raw.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        const items: { title: string; description?: string }[] = [];
        let currentItem: { title: string; description?: string } | null = null;

        lines.forEach(line => {
          if (line.toLowerCase().startsWith('title:') || line.startsWith('-') || line.startsWith('*') || /^\d+\./.test(line)) {
            if (currentItem) items.push(currentItem);
            const cleaned = line.replace(/^(title:|-|\*|\d+\.)\s*/i, '').trim();
            const parts = cleaned.split(':');
            if (parts.length > 1) {
              currentItem = { title: parts[0].trim(), description: parts.slice(1).join(':').trim() };
            } else {
              currentItem = { title: cleaned };
            }
          } else if (currentItem) {
            currentItem.description = (currentItem.description ? currentItem.description + ' ' : '') + line;
          }
        });
        if (currentItem) items.push(currentItem);
        parsed = items;
      }

      if (!Array.isArray(parsed) || parsed.length === 0) {
        setAutoFillStatus('⚠️ Could not find valid pain points in pasted text. Please paste JSON or bullet list.');
        return;
      }

      const newPainPoints: PagePainPoint[] = parsed.map((item: any, idx: number) => ({
        id: `pain-auto-${Date.now()}-${idx}`,
        title: item.title || item.name || `Pain Point ${idx + 1}`,
        description: item.description || item.desc || '',
      }));

      const existing = selectedPage.painPoints || [];
      const merged = [...existing, ...newPainPoints];

      updateCurrentPage({ painPoints: merged });
      setPastedGptOutput('');
      setAutoFillStatus(`✅ Successfully auto-filled ${newPainPoints.length} pain points!`);
      setTimeout(() => setAutoFillStatus(null), 4000);
    } catch {
      setAutoFillStatus('⚠️ Error parsing output. Please ensure text matches JSON format.');
    }
  };

  const selectedStatus = getPageResearchStatus(selectedPage);

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1280px', margin: '0 auto' }}>
      
      {/* 1. PAGE SELECTOR TAB BAR */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>
          Select Planned Page to Research:
        </label>
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
          {pages.map((p, idx) => {
            const isSelected = p.id === selectedPage.id;
            const statusInfo = getPageResearchStatus(p);
            const compCount = p.competitorHeadings?.filter(c => (c.headings && c.headings.length > 0) || c.nameOrUrl?.trim()).length || 0;
            const painCount = p.painPoints?.length || 0;

            return (
              <button
                key={p.id}
                onClick={() => setSelectedPageId(p.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 16px',
                  borderRadius: '12px',
                  border: isSelected ? '1.5px solid #0284c7' : '1px solid #1e293b',
                  background: isSelected ? 'rgba(2, 132, 199, 0.18)' : '#070c16',
                  color: isSelected ? '#ffffff' : '#94a3b8',
                  fontSize: '13px',
                  fontWeight: isSelected ? 700 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                }}
              >
                <span>#{idx + 1} {p.pageName}</span>
                <span
                  style={{
                    fontSize: '10.5px',
                    background: statusInfo.bg,
                    color: statusInfo.color,
                    border: `1px solid ${statusInfo.border}`,
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontWeight: 700,
                  }}
                >
                  ● {statusInfo.label} ({compCount} Comps • {painCount} Pains)
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. CURRENT PAGE RESEARCH HEADER & GOOGLE SEARCH BUTTON */}
      <div
        style={{
          background: '#090e1a',
          border: '1px solid #1e293b',
          borderRadius: '16px',
          padding: '24px 28px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
              {selectedPage.pageType || 'Service Page'}
            </span>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
              Target URL: <strong style={{ color: '#38bdf8', fontFamily: 'var(--font-mono)' }}>{selectedPage.targetUrl}</strong>
            </span>
            <span style={{ fontSize: '11px', fontWeight: 700, color: selectedStatus.color, background: selectedStatus.bg, border: `1px solid ${selectedStatus.border}`, padding: '2px 8px', borderRadius: '6px' }}>
              Research Status: {selectedStatus.label}
            </span>
          </div>

          <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#ffffff', margin: '0 0 8px 0', letterSpacing: '-0.01em' }}>
            {selectedPage.pageName}
          </h2>

          <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ color: '#94a3b8', fontWeight: 600 }}>Default SERP Seed Keyword:</span>
            <span style={{ fontSize: '15px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(56, 189, 248, 0.25)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              🎯 {selectedPage.primaryKeyword || 'Not set'}
              <span style={{ fontSize: '10px', fontWeight: 700, background: '#0284c7', color: '#ffffff', padding: '1px 6px', borderRadius: '4px' }}>SEED</span>
            </span>
          </div>

          {(selectedPage.secondaryKeyword || selectedPage.tertiaryKeyword) && (
            <div style={{ marginTop: '10px', display: 'flex', gap: '14px', fontSize: '12px', color: '#94a3b8', flexWrap: 'wrap' }}>
              {selectedPage.secondaryKeyword && (
                <span><strong style={{ color: '#f59e0b' }}>Secondary (Supporting Context):</strong> {selectedPage.secondaryKeyword}</span>
              )}
              {selectedPage.tertiaryKeyword && (
                <span><strong style={{ color: '#a855f7' }}>Tertiary (Supporting Context):</strong> {selectedPage.tertiaryKeyword}</span>
              )}
            </div>
          )}
        </div>

        {/* Search on Google Trigger Button */}
        <div>
          <button
            onClick={handleOpenGoogleSearch}
            disabled={!selectedPage.primaryKeyword}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 22px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              color: '#ffffff',
              fontSize: '13.5px',
              fontWeight: 800,
              border: 'none',
              cursor: selectedPage.primaryKeyword ? 'pointer' : 'not-allowed',
              boxShadow: '0 4px 20px rgba(2, 132, 199, 0.35)',
              opacity: selectedPage.primaryKeyword ? 1 : 0.5,
              transition: 'all 0.15s ease',
            }}
          >
            <Search size={18} />
            <span>Search on Google</span>
            <ExternalLink size={14} />
          </button>
        </div>
      </div>

      {/* 3. METHODOLOGY BANNER */}
      <div
        style={{
          background: 'rgba(245, 158, 11, 0.08)',
          border: '1px solid rgba(245, 158, 11, 0.25)',
          borderRadius: '12px',
          padding: '14px 20px',
          marginBottom: '28px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Sparkles size={20} color="#f59e0b" style={{ flexShrink: 0 }} />
        <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.5' }}>
          <strong style={{ color: '#ffffff' }}>Competitor Heading Research Method:</strong>{' '}
          Search Google for <span style={{ color: '#38bdf8', fontWeight: 700 }}>"{selectedPage.primaryKeyword || selectedPage.pageName}"</span>, identify relevant competing websites, and record their H1, H2, and H3 headings.
          These headings are automatically merged into the <span style={{ color: '#f59e0b', fontWeight: 700 }}>Combined Competitor Headings Dataset</span> to power Step 3 gap analysis.
        </div>
      </div>

      {/* 4. TWO COLUMNS GRID: COMPETITOR HEADINGS INPUT + COMBINED DATASET & PAIN POINTS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
        
        {/* LEFT COLUMN: RELEVANT SERP COMPETITOR HEADINGS */}
        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Layers size={18} color="#38bdf8" />
                <span>Relevant SERP Competitor Headings</span>
              </h3>
              <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
                Enter headings from top ranking competitors. Up to 10 entries supported (fill as many relevant competitors as available).
              </p>
            </div>
          </div>

          {/* 10 Competitors Form List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(compNum => {
              const compData = competitorHeadings.find(c => c.competitorNumber === compNum);
              return (
                <CompetitorCardInput
                  key={`${selectedPage.id}-comp-${compNum}`}
                  pageId={selectedPage.id}
                  compNum={compNum}
                  compData={compData}
                  onUpdate={handleUpdateCompetitor}
                />
              );
            })}
          </div>
        </div>

        {/* RIGHT COLUMN: COMBINED COMPETITOR HEADINGS DATASET & CUSTOMER PAIN POINTS */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* CARD A: COMBINED COMPETITOR HEADINGS DATASET */}
          <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ListOrdered size={18} color="#38bdf8" />
                  <span>Combined Competitor Headings Dataset</span>
                </h3>
                <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
                  Consolidated H1/H2/H3 headings across all research entries for Step 3 synthesis.
                </p>
              </div>

              <button
                onClick={handleCopyCombinedHeadings}
                disabled={combinedHeadingsList.length === 0}
                style={{
                  background: copiedCombined ? 'rgba(16, 185, 129, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                  border: copiedCombined ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(56, 189, 248, 0.3)',
                  color: copiedCombined ? '#10b981' : '#38bdf8',
                  fontSize: '12px',
                  fontWeight: 700,
                  padding: '6px 12px',
                  borderRadius: '8px',
                  cursor: combinedHeadingsList.length > 0 ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: combinedHeadingsList.length > 0 ? 1 : 0.5,
                }}
              >
                {copiedCombined ? <Check size={14} /> : <Copy size={14} />}
                <span>{copiedCombined ? 'Copied Dataset!' : 'Copy Combined Headings'}</span>
              </button>
            </div>

            {combinedHeadingsList.length === 0 ? (
              <div
                style={{
                  background: '#090e1a',
                  border: '1px dashed #1e293b',
                  borderRadius: '12px',
                  padding: '24px 16px',
                  textAlign: 'center',
                  fontSize: '12.5px',
                  color: '#64748b',
                }}
              >
                No competitor headings added yet. Add headings on the left to compile your combined dataset.
              </div>
            ) : (
              <div
                style={{
                  background: '#090e1a',
                  border: '1px solid #1e293b',
                  borderRadius: '12px',
                  padding: '16px',
                  maxHeight: '320px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <div style={{ fontSize: '11px', color: '#38bdf8', fontWeight: 700, marginBottom: '4px' }}>
                  Total {combinedHeadingsList.length} Headings Consolidated:
                </div>
                {combinedHeadingsList.map((hLine, hIdx) => {
                  const tag = hLine.split(':')[0] || 'H2';
                  const content = hLine.substring(hLine.indexOf(':') + 1).trim();
                  return (
                    <div
                      key={hIdx}
                      style={{
                        fontSize: '12px',
                        color: '#cbd5e1',
                        background: '#070c16',
                        border: '1px solid #1e293b',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontFamily: 'monospace',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 800,
                          color: tag === 'H1' ? '#38bdf8' : tag === 'H2' ? '#f59e0b' : '#a855f7',
                          background: 'rgba(15, 23, 42, 0.9)',
                          border: '1px solid #1e293b',
                          padding: '1px 6px',
                          borderRadius: '4px',
                        }}
                      >
                        {tag}
                      </span>
                      <span style={{ flex: 1 }}>{content}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* CARD B: CUSTOMER PAIN POINTS / RESEARCH INSIGHTS */}
          <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Sparkles size={18} color="#f59e0b" />
                  <span>Customer Pain Points / Research Insights</span>
                </h3>
                <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
                  Collect customer anxieties, risks, and frustrations as an additional research input for Step 3.
                </p>
              </div>
              <Button size="sm" onClick={() => setIsAddPainModalOpen(true)} icon={<Plus size={14} />}>
                Add Pain Point
              </Button>
            </div>

            {/* GPT Prompt Assistant Box */}
            <div
              style={{
                background: '#090e1a',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <Sparkles size={14} />
                  <span>GPT / AI Research Prompt Helper</span>
                </div>
                <button
                  onClick={handleCopyGptPrompt}
                  style={{
                    background: copiedPrompt ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                    border: copiedPrompt ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(245, 158, 11, 0.3)',
                    color: copiedPrompt ? '#10b981' : '#f59e0b',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    padding: '4px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {copiedPrompt ? <Check size={13} /> : <Copy size={13} />}
                  <span>{copiedPrompt ? 'Copied Prompt!' : 'Copy GPT Prompt'}</span>
                </button>
              </div>

              <p style={{ fontSize: '12px', color: '#cbd5e1', margin: '0 0 10px 0', lineHeight: 1.5, background: '#070c16', padding: '10px 12px', borderRadius: '8px', border: '1px solid #1e293b', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                {gptPromptText}
              </p>
              <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 12px 0' }}>
                💡 Copy this prompt into ChatGPT/Gemini/Claude, then paste the returned JSON below for 1-click auto-fill.
              </p>

              {/* Hallucination control note */}
              <div style={{ fontSize: '11px', color: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)', padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(245, 158, 11, 0.2)', marginBottom: '12px' }}>
                ℹ️ Treat AI-generated insights as research hypotheses for customer intent, not verified customer testimonials.
              </div>

              {/* Paste GPT Output Field & Auto-Fill Button */}
              <div style={{ paddingTop: '12px', borderTop: '1px solid #1e293b' }}>
                <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#f59e0b', marginBottom: '6px' }}>
                  Paste GPT Output to Auto-Fill Pain Points:
                </label>
                <textarea
                  rows={3}
                  placeholder={`Paste JSON response here...\ne.g. [\n  { "title": "Long shipping delays", "description": "Commercial fleets lose money..." }\n]`}
                  value={pastedGptOutput}
                  onChange={e => setPastedGptOutput(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    background: '#070c16',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    marginBottom: '8px',
                    resize: 'vertical',
                  }}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleAutoFillPainPoints}
                    disabled={!pastedGptOutput.trim()}
                    style={{
                      background: pastedGptOutput.trim() ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : '#1e293b',
                      color: pastedGptOutput.trim() ? '#070c16' : '#64748b',
                      fontSize: '12px',
                      fontWeight: 800,
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: pastedGptOutput.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Sparkles size={14} />
                    <span>Auto-Fill Pain Points</span>
                  </button>

                  {autoFillStatus && (
                    <span style={{ fontSize: '11.5px', fontWeight: 700, color: autoFillStatus.startsWith('✅') ? '#10b981' : '#f59e0b' }}>
                      {autoFillStatus}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Pain Points List */}
            {painPoints.length === 0 ? (
              <div
                style={{
                  background: '#090e1a',
                  border: '1px border-dashed #1e293b',
                  borderRadius: '12px',
                  padding: '32px 16px',
                  textAlign: 'center',
                }}
              >
                <AlertTriangle size={28} color="#64748b" style={{ margin: '0 auto 8px auto' }} />
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginBottom: '4px' }}>
                  No Pain Points Added for this Page
                </div>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px 0' }}>
                  Use the GPT prompt helper above to find customer pain points and add them here.
                </p>
                <Button size="sm" onClick={() => setIsAddPainModalOpen(true)} icon={<Plus size={14} />}>
                  Add Pain Point
                </Button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {painPoints.map((pain) => (
                  <div
                    key={pain.id}
                    style={{
                      background: '#090e1a',
                      border: '1px solid #1e293b',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '12px',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#ffffff', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#f59e0b' }}>●</span>
                        <span>{pain.title}</span>
                      </div>
                      {pain.description && (
                        <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0, lineHeight: 1.4 }}>
                          {pain.description}
                        </p>
                      )}
                    </div>

                    <button
                      onClick={() => handleDeletePainPoint(pain.id)}
                      title="Delete Pain Point"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#64748b',
                        cursor: 'pointer',
                        padding: '4px',
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

      </div>

      {/* 5. ADD PAIN POINT MODAL */}
      {isAddPainModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 110,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#090e1a',
              border: '1px solid #1e293b',
              borderRadius: '14px',
              width: '100%',
              maxWidth: '480px',
              padding: '24px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)',
            }}
          >
            <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 6px 0' }}>
              Add Customer Pain Point / Research Insight
            </h3>
            <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: '0 0 16px 0' }}>
              Page: <strong style={{ color: '#ffffff' }}>{selectedPage.pageName}</strong>
            </p>

            <form onSubmit={handleAddPainPoint} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                  Pain Point Title / Frustration <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="e.g. Long shipping delays for heavy-duty truck parts in Trinidad"
                  value={newPainTitle}
                  onChange={e => setNewPainTitle(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    background: '#070c16',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '13px',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
                  Description / Context (Optional)
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Commercial fleet operators face severe revenue loss when trucks are grounded waiting for imported spare parts."
                  value={newPainDesc}
                  onChange={e => setNewPainDesc(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    background: '#070c16',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '13px',
                    resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                <Button type="button" variant="secondary" onClick={() => setIsAddPainModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Save Research Insight
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
